import { showToast } from '../../../engine/toast';
import {
  adminStats, adminServerStatus, adminGetPaymentConfig, adminPatchPaymentConfig, adminGetSettings,
  FEATURE_KYC, FEATURE_WITHDRAWALS,
} from '../admin-api';
import { escapeHtml, statTile, tileGrid, toggleSwitch } from '../admin-ui';
import type { PaneCtx, PaneModule } from './types';

function fmt(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && typeof value !== 'boolean' ? n.toLocaleString('en-US') : String(value ?? '—');
}

function cents(value: unknown): string {
  const n = Number(value);
  return Number.isFinite(n) ? `${(n / 100).toFixed(2)}` : '—';
}

/**
 * Tiles for a FLAT record — used for `server-status`, whose keys vary by
 * deployment. Nested values are skipped: they are objects with no single
 * headline number, and stringified JSON in a stat tile is noise.
 */
function flatTiles(src: Record<string, unknown> | null): string {
  if (!src) return '<div class="text-xs text-text-dim">Loading&hellip;</div>';
  const entries = Object.entries(src).filter(([, v]) => typeof v !== 'object' || v === null);
  if (entries.length === 0) return '<div class="text-xs text-text-dim">Nothing reported.</div>';
  return tileGrid(
    entries
      .map(([key, value]) =>
        statTile(key.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2'), fmt(value)),
      )
      .join(''),
  );
}

/**
 * `GET /admin/stats/overview` answers in NESTED sections — `players`,
 * `economy`, `revenue`, `pending`, plus arrays for the activity chart and the
 * recent lists. A generic flattener drops every one of them (which is why this
 * pane read "Nothing reported" before), so the sections that matter are named
 * explicitly and given real labels. Money is EUR cents on the wire.
 */
function overviewTiles(src: Record<string, unknown> | null): string {
  if (!src) return '<div class="text-xs text-text-dim">Loading&hellip;</div>';
  const section = (name: string) => (src[name] ?? {}) as Record<string, unknown>;
  const players = section('players');
  const economy = section('economy');
  const revenue = section('revenue');
  const pending = section('pending');

  const groups: { heading: string; tiles: string }[] = [
    {
      heading: 'Players',
      tiles: [
        statTile('Total', fmt(players.total)),
        statTile('Online now', fmt(players.online)),
        statTile('New today', fmt(players.newToday)),
        statTile('New this week', fmt(players.newThisWeek)),
      ].join(''),
    },
    {
      heading: 'Pending',
      tiles: [
        // A non-empty queue is the one thing here an operator must act on.
        statTile('KYC waiting', fmt(pending.kyc), Number(pending.kyc) > 0),
        statTile('Withdrawals', fmt(pending.withdrawals), Number(pending.withdrawals) > 0),
        statTile('Withdrawal value', cents(pending.withdrawalsCents)),
      ].join(''),
    },
    {
      heading: 'Economy',
      tiles: [
        statTile('Soft currency', fmt(economy.totalSoft)),
        statTile('Delta-V', fmt(economy.totalPremium)),
        statTile('Average / player', fmt(economy.avgPerUser)),
      ].join(''),
    },
    {
      heading: 'Revenue (EUR)',
      tiles: [
        statTile('Total', cents(revenue.totalCents)),
        statTile('Today', cents(revenue.todayCents)),
        statTile('Last 7 days', cents(revenue.last7dCents)),
        statTile('Payments', fmt(revenue.paymentsTotal)),
      ].join(''),
    },
  ];

  return groups
    .map(
      (g) => `
      <div>
        <h3 class="text-[10px] text-text-dim uppercase tracking-wide mb-1.5">${g.heading}</h3>
        ${tileGrid(g.tiles)}
      </div>`,
    )
    .join('');
}

function switchesHtml(ctx: PaneCtx): string {
  const { settings, paymentConfig: cfg } = ctx.state.overview;
  if (!settings) return '<div class="text-xs text-text-dim">Loading&hellip;</div>';

  // Stored values, not the config endpoint's effective ones — see
  // `adminGetSettings` for why binding to the latter makes the withdrawals
  // switch look broken.
  const withdrawalsStored = settings[FEATURE_WITHDRAWALS] === 'true';
  const kycStored = settings[FEATURE_KYC] === 'true';

  // When payments are not adopted, core treats the whole surface as preview:
  // the flag is honoured in storage but nothing acts on it. Saying so beats
  // a switch that silently means nothing.
  const inactive = cfg ? cfg.adopted === false : false;

  return `
    <div class="glass-panel px-3 py-1 divide-y divide-border">
      ${toggleSwitch(
        'toggle-withdrawals',
        'Withdrawals',
        'Lets players request a payout. Off hides the Withdrawals tab and refuses the endpoint.',
        withdrawalsStored,
        ctx.state.busy,
      )}
      ${toggleSwitch(
        'toggle-kyc',
        'KYC',
        'Requires an approved identity check before a player can withdraw.',
        kycStored,
        ctx.state.busy,
      )}
    </div>
    ${
      inactive && withdrawalsStored
        ? `<p class="mt-1 text-[10px] text-gold leading-relaxed">
             Withdrawals are switched on but stay inactive: this server has not adopted the payments module,
             so the payout endpoints answer <code>PAYMENTS_NOT_ADOPTED</code>. The setting is saved and will
             take effect once a payment ledger is registered.
           </p>`
        : ''
    }`;
}

export const overviewPane: PaneModule = {
  id: 'overview',
  label: 'Overview',

  html(ctx) {
    return `
      <section class="flex flex-col gap-4">
        <div class="flex flex-col gap-3">
          <h2 class="font-heading text-xs text-gold tracking-wide uppercase">Statistics</h2>
          ${overviewTiles(ctx.state.overview.stats)}
        </div>
        <div>
          <h2 class="font-heading text-xs text-gold mb-1.5 tracking-wide uppercase">Server</h2>
          ${flatTiles(ctx.state.overview.serverStatus)}
        </div>
        <div>
          <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">Features</h2>
          ${switchesHtml(ctx)}
        </div>
      </section>`;
  },

  async load(ctx) {
    const [s, st, cfg, settings] = await Promise.allSettled([
      adminStats(),
      adminServerStatus(),
      adminGetPaymentConfig(),
      adminGetSettings(),
    ]);
    if (ctx.disposed()) return;
    ctx.state.overview.stats = s.status === 'fulfilled' ? (s.value as Record<string, unknown>) : {};
    ctx.state.overview.serverStatus = st.status === 'fulfilled' ? (st.value as Record<string, unknown>) : {};
    // Left null on failure so the switches render "Loading…" rather than
    // claiming both features are off — which an operator could act on.
    ctx.state.overview.paymentConfig = cfg.status === 'fulfilled' ? cfg.value : null;
    ctx.state.overview.settings = settings.status === 'fulfilled' ? settings.value : null;
    if (settings.status === 'rejected') {
      showToast('Could not read the feature settings.', 'error');
    }
  },

  onAction(action, _el, ctx) {
    if (action !== 'toggle-withdrawals' && action !== 'toggle-kyc') return false;
    const settings = ctx.state.overview.settings;
    if (!settings) return true;
    const isKyc = action === 'toggle-kyc';
    const settingKey = isKyc ? FEATURE_KYC : FEATURE_WITHDRAWALS;
    const next = settings[settingKey] !== 'true';

    void ctx.withBusy(async () => {
      await adminPatchPaymentConfig({ [isKyc ? 'kycEnabled' : 'withdrawalsEnabled']: next });
      if (ctx.disposed()) return;
      // Re-read the STORED settings. The PATCH response is the effective
      // snapshot, and for withdrawals that is masked to false whenever
      // payments are unadopted — adopting it here would bounce the switch
      // straight back off after a save that actually succeeded.
      ctx.state.overview.settings = await adminGetSettings();
      showToast(`${isKyc ? 'KYC' : 'Withdrawals'} ${next ? 'enabled' : 'disabled'}.`, 'success');
    }, 'Could not change that setting.');
    return true;
  },
};

/** Exported for the tests that pin the nested-section mapping. */
export const __testables = { overviewTiles, flatTiles, fmt, cents, escapeHtml };
